/**
 * Query params for GET /api/v1/energy/dashboard, built from the global filters.
 *
 * A filter is sent whenever the selection is a strict subset of everything the
 * filter offers (audit F01 / ISS-187: the Models filter used to be sent only for
 * 1 or 2 models, so 3 to 17 of 18 silently showed the whole fleet). Selecting
 * everything sends no filter, matching "all" in the filter menus.
 */
import { MODEL_LABELS, INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import type { IndustryType } from '@/types';

interface DashboardGroup {
  id: string;
  parent_group_id?: string | null;
  start_date?: string | null;
  end_date?: string | null;
}

export interface EnergyDashboardParamsInput {
  /** '7d' | '30d' | ... or 'production' (use the selected production's dates). */
  dateRange: string;
  groups: DashboardGroup[];
  selectedCompanyIds: string[];
  /** How many companies the company filter offers. */
  companyCount: number;
  selectedGroupIds: string[];
  selectedIndustryTypes: IndustryType[];
  selectedModels: string[];
  /** Today as YYYY-MM-DD (an open-ended production runs to today). */
  today: string;
}

const ALL_MODELS = Object.keys(MODEL_LABELS);

/** True when `selected` is non-empty and leaves out at least one of `all`. */
function isStrictSubset(selected: readonly string[], all: readonly string[]): boolean {
  if (selected.length === 0) return false;
  const chosen = new Set(selected);
  return all.some((value) => !chosen.has(value));
}

export function buildEnergyDashboardParams(input: EnergyDashboardParamsInput): URLSearchParams {
  const params = new URLSearchParams();

  // 'production' uses the dates of the one selected production (a top-level
  // group), whatever child sites are also selected.
  const selectedProductions = input.groups.filter(
    (g) => input.selectedGroupIds.includes(g.id) && !g.parent_group_id,
  );
  // An open-ended production runs to today; one that has not started yet ends
  // on its own start day (no data yet), so the window never ends before it
  // begins. A stored end before the start is bad data: fall back to 30 days.
  const production = selectedProductions.length === 1 ? selectedProductions[0] : null;
  const start = production?.start_date ?? null;
  const end = start ? (production?.end_date || (input.today > start ? input.today : start)) : null;
  if (input.dateRange === 'production' && start && end && end >= start) {
    params.set('startDate', start);
    params.set('endDate', end);
  } else {
    params.set('dateRange', input.dateRange === 'production' ? '30d' : input.dateRange);
  }

  if (input.selectedCompanyIds.length > 0 && input.selectedCompanyIds.length < input.companyCount) {
    params.set('companyIds', input.selectedCompanyIds.join(','));
  }
  if (input.selectedGroupIds.length > 0) {
    params.set('groupIds', input.selectedGroupIds.join(','));
  }
  if (isStrictSubset(input.selectedIndustryTypes, INDUSTRY_TYPE_ORDER)) {
    params.set('industryTypes', input.selectedIndustryTypes.join(','));
  }
  if (isStrictSubset(input.selectedModels, ALL_MODELS)) {
    params.set('models', input.selectedModels.join(','));
  }
  return params;
}
